import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ALTabsComponent } from '@asamco/application-link/shared';
import { AsamcoUserService, Permissions } from '@asamco/base';
import { Group, GroupService, Permission, PermissionService, UserInGroupDto } from '@asamco/user-management/shared';
import { showErrorSwal } from '@asamco/utils';
import { DxButtonModule, DxCheckBoxModule, DxDataGridModule, DxPopupModule, DxTextBoxModule } from 'devextreme-angular';
import { DxiColumnModule, DxoFilterRowModule, DxoPagerModule, DxoPagingModule, DxoSearchPanelModule, DxoSelectionModule, DxoSortingModule } from 'devextreme-angular/ui/nested';
import notify from 'devextreme/ui/notify';
import { SelectionChangedEvent } from 'devextreme/ui/data_grid';
import { BehaviorSubject, Observable, catchError, combineLatest, firstValueFrom, map, of, switchMap } from 'rxjs';

type GroupDetailsTabId = 'information' | 'users' | 'permissions';

type PermissionGridRow = Permission & {
	category: string;
	permissionCode: string;
	displayPermissionName: string;
	displayPermissionDescription: string;
};

type GroupDetailsViewModel = {
	group: Group;
	users: UserInGroupDto[];
};

type TabInfo = {
	id: GroupDetailsTabId;
	text: string;
	icon: string;
};

const protectedDefaultGroupNames = new Set([
	'Super ApplicationLink Administrators',
	'ApplicationLink Admins',
]);

const applicationLinkPermissionCodes = new Map<string, string>([
	['839cd3d1-fb13-437a-872b-32c663de2a28', 'AL3_IMPERSONATION'],
	['2bf719b0-712b-41f7-b731-989f35949a31', 'AL3_FUNC_APP'],
	['1f25b2f7-9e3d-4b4a-a3c7-7ecf73ec7e91', 'AL3_TEST_PERMISSION'],
]);

const permissionCodesByKey = new Map<string, string>([
	...Object.entries(Permissions).map(([permissionCode, permissionKey]) => [permissionKey.toLowerCase(), permissionCode] as const),
	...applicationLinkPermissionCodes.entries(),
]);

const permissionDisplayNamesByCode = new Map<string, string>([
	['AL3_ADMINISTRATION', 'Administration Pages'],
	['AL3_USERMANAGEMENT_ASSIGN', 'Assign User Management Permissions'],
	['AL3_USERCOMPANY_VIEW', 'User Companies'],
	['AL3_USERCOMPANY_MANAGE', 'Manage User Companies'],
	['AL3_USER_MANAGE', 'Manage Users'],
	['AL3_COMPANY_REQUESTS_HANDLE', 'Handle Company Requests'],
	['AL3_COMPANY_ACCESS_REQUESTS_HANDLE', 'Company Access Requests'],
	['AL3_COMPANY_VIEW', 'View Companies'],
	['AL3_COMPANY_MANAGE', 'Manage Companies'],
	['AL3_MARKETPLACE', 'Marketplace'],
	['AL3_ADMIN_LINK_OVERVIEW', 'Link Operations'],
	['AL3_MAINTENANCE_VIEW', 'Maintenance Queue Blocker'],
	['AL3_MAINTENANCE_EDIT', 'Edit Maintenance'],
	['AL3_ADMIN_TOOLING', 'Admin Tooling'],
	['AL3_TICKET_INTEGRATION', 'Ticket Integration'],
	['AP_VIEW_APPLICATION_LOGS', 'Application Logs'],
	['AL3_JOBLOG_PATTERN_MATCHER', 'Job Log Pattern Matcher'],
	['AL3_CHANGELOG', 'Changelog'],
	['AL3_APPLICATION_MANAGEMENT', 'Applications'],
	['AL3_LINK_TRANSFER', 'Link Transfer'],
	['AL3_MOCK_DATA', 'Mock Data'],
	['AL3_WARNING_MODAL_MESSAGES', 'Warning Modal Messages'],
	['AL3_FUNC_APP', 'Function App Management'],
	['AL3_INT_TEST', 'Integration Tester'],
	['AS_UM_MI_USER_MANAGEMENT_MENU_ITEM', 'User Management Menu'],
	['AS_UM_MI_USERS', 'Users'],
	['AS_UM_MI_GROUPS', 'Groups'],
]);

@Component({
	selector: 'app-shell-group-details',
	templateUrl: './group-details.component.html',
	styleUrls: ['./group-details.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		ALTabsComponent,
		AsyncPipe,
		DxButtonModule,
		DxCheckBoxModule,
		DxDataGridModule,
		DxPopupModule,
		DxiColumnModule,
		DxoFilterRowModule,
		DxoPagerModule,
		DxoPagingModule,
		DxoSearchPanelModule,
		DxoSelectionModule,
		DxoSortingModule,
		DxTextBoxModule,
		FormsModule,
		NgFor,
		NgIf,
	],
})
export class GroupDetailsComponent implements OnInit {
	public readonly tabInfo: TabInfo[] = [
		{ id: 'information', text: 'Information', icon: 'far fa-info-circle' },
		{ id: 'users', text: 'Users', icon: 'far fa-user' },
		{ id: 'permissions', text: 'Permissions', icon: 'far fa-user-shield' },
	];

	public readonly selectedTabId$ = new BehaviorSubject<GroupDetailsTabId>('information');
	public readonly saving$ = new BehaviorSubject<boolean>(false);
	public readonly searchValue$ = new BehaviorSubject<string>('');
	public viewModel$!: Observable<GroupDetailsViewModel>;
	public formModel: Group = {};
	public permissionRows: PermissionGridRow[] = [];
	public assignedSelectedRowKeys: string[] = [];
	public availableSelectedRowKeys: string[] = [];
	public canManageGroupPermissions = false;
	public canManageDefaultGroupSetting = false;
	public permissionReviewVisible = false;
	public permissionReviewAddedPermissions: PermissionGridRow[] = [];
	public permissionReviewRemovedPermissions: PermissionGridRow[] = [];
	public permissionReviewAffectedUsers: UserInGroupDto[] = [];

	private groupId!: number;
	private originalGroup: Group = {};
	private originalPermissionStates = new Map<string, boolean>();
	private currentGroupUsers: UserInGroupDto[] = [];

	constructor(
		private activatedRoute: ActivatedRoute,
		private asamcoUserService: AsamcoUserService,
		private changeDetectorRef: ChangeDetectorRef,
		private groupService: GroupService,
		private permissionService: PermissionService,
		private router: Router,
	) {}

	ngOnInit(): void {
		void this.initializePermissions();
		this.viewModel$ = this.activatedRoute.paramMap.pipe(
			map(params => Number(params.get('id'))),
			switchMap(groupId => {
				this.groupId = groupId;
				return combineLatest([
					this.groupService.get(groupId),
					this.permissionService.getGroupPermissions(groupId).pipe(catchError(() => of([]))),
					this.groupService.getUsersPerGroup('', 0).pipe(catchError(() => of([]))),
				]);
			}),
			map(([group, permissions, users]) => {
				this.currentGroupUsers = users.filter(user => user.groupId === this.groupId);
				this.formModel = { ...group };
				this.originalGroup = { ...group };
				this.permissionRows = this.toPermissionGridRows(permissions);
				this.originalPermissionStates = new Map(this.permissionRows.map(permission => [permission.permissionKey, permission.isAllowed]));
				this.assignedSelectedRowKeys = [];
				this.availableSelectedRowKeys = [];

				return {
					group,
					users: this.currentGroupUsers,
				};
			}),
		);
	}

	private async initializePermissions(): Promise<void> {
		const assignedPermissions = await this.asamcoUserService.getPermissions();
		const canAssignPermissions = assignedPermissions.includes(Permissions.PERMISSION_ASSIGN.toLowerCase());
		const canRevokePermissions = assignedPermissions.includes(Permissions.PERMISSION_REVOKE.toLowerCase());
		this.canManageGroupPermissions = canAssignPermissions && canRevokePermissions;
		this.canManageDefaultGroupSetting = this.canManageGroupPermissions;
		this.changeDetectorRef.markForCheck();
	}

	public readonly onTabClick = (tab: TabInfo) => {
		this.selectedTabId$.next(tab.id);
	};

	public currentTabLabel(selectedTabId: GroupDetailsTabId): string {
		return this.tabInfo.find(tab => tab.id === selectedTabId)?.text ?? 'Details';
	}

	public async saveGroup(skipPermissionReview = false): Promise<void> {
		if (!skipPermissionReview && this.canManageGroupPermissions && this.hasPermissionChanges()) {
			this.openPermissionReview();
			return;
		}

		this.saving$.next(true);

		try {
			let didSave = false;

			if (this.hasGroupChanges()) {
				const saved = await firstValueFrom(this.groupService.save(this.formModel));
				this.formModel = { ...saved };
				this.originalGroup = { ...saved };
				didSave = true;
			}

			if (this.hasPermissionChanges()) {
				await this.savePermissionChanges();
				didSave = true;
			}

			if (didSave) {
				notify('Changes saved successfully.', 'success', 2000);
			} else {
				notify('No changes to save.', 'info', 1500);
			}
		} catch (error) {
			showErrorSwal(error);
		} finally {
			this.saving$.next(false);
		}
	}

	public hasPendingChanges(): boolean {
		return this.hasGroupChanges() || (this.canManageGroupPermissions && this.hasPermissionChanges());
	}

	public canChangeDefaultGroupSetting(): boolean {
		return this.canManageDefaultGroupSetting && !this.isProtectedDefaultGroup(this.formModel);
	}

	public stagePermission(permission: Permission, value: boolean): void {
		permission.isAllowed = value;
	}

	public onAssignedSelectionChanged(event: SelectionChangedEvent): void {
		this.assignedSelectedRowKeys = this.toPermissionKeys(event.selectedRowKeys);
	}

	public onAvailableSelectionChanged(event: SelectionChangedEvent): void {
		this.availableSelectedRowKeys = this.toPermissionKeys(event.selectedRowKeys);
	}

	public assignSelectedPermissions(permissions: PermissionGridRow[]): void {
		if (!this.canManageGroupPermissions) {
			return;
		}

		const selectedPermissions = permissions.filter(permission => this.availableSelectedRowKeys.includes(permission.permissionKey));
		if (selectedPermissions.length === 0) {
			return;
		}

		for (const permission of selectedPermissions) {
			this.stagePermission(permission, true);
		}

		this.availableSelectedRowKeys = [];
	}

	public removeSelectedPermissions(permissions: PermissionGridRow[]): void {
		if (!this.canManageGroupPermissions) {
			return;
		}

		const selectedPermissions = permissions.filter(permission => this.assignedSelectedRowKeys.includes(permission.permissionKey));
		if (selectedPermissions.length === 0) {
			return;
		}

		for (const permission of selectedPermissions) {
			this.stagePermission(permission, false);
		}

		this.assignedSelectedRowKeys = [];
	}

	public filteredPermissions(permissions: PermissionGridRow[], searchValue: string): PermissionGridRow[] {
		const normalizedSearch = searchValue.trim().toLowerCase();
		return permissions.filter(permission => !normalizedSearch || [permission.displayPermissionName, permission.permissionCode, permission.permissionKey, permission.displayPermissionDescription, permission.category]
			.filter(value => !!value)
			.some(value => value.toLowerCase().includes(normalizedSearch)));
	}

	public assignedPermissions(permissions: PermissionGridRow[]): PermissionGridRow[] {
		return permissions.filter(permission => permission.isAllowed);
	}

	public availablePermissions(permissions: PermissionGridRow[]): PermissionGridRow[] {
		return permissions.filter(permission => !permission.isAllowed);
	}

	public async back(): Promise<void> {
		await this.router.navigate(['../'], { relativeTo: this.activatedRoute });
	}

	public confirmPermissionReview(): void {
		this.permissionReviewVisible = false;
		void this.saveGroup(true);
	}

	public closePermissionReview(): void {
		if (this.saving$.value) {
			return;
		}

		this.permissionReviewVisible = false;
	}

	private openPermissionReview(): void {
		this.permissionReviewAddedPermissions = this.permissionRows.filter(permission => this.originalPermissionStates.get(permission.permissionKey) === false && permission.isAllowed);
		this.permissionReviewRemovedPermissions = this.permissionRows.filter(permission => this.originalPermissionStates.get(permission.permissionKey) === true && !permission.isAllowed);
		this.permissionReviewAffectedUsers = this.currentGroupUsers;
		this.permissionReviewVisible = true;
	}

	private hasGroupChanges(): boolean {
		return this.formModel.name !== this.originalGroup.name
			|| this.formModel.description !== this.originalGroup.description
			|| this.formModel.isDefaultGroup !== this.originalGroup.isDefaultGroup;
	}

	private isProtectedDefaultGroup(group: Group): boolean {
		return !!group.name && protectedDefaultGroupNames.has(group.name);
	}

	private hasPermissionChanges(): boolean {
		return this.permissionRows.some(permission => this.originalPermissionStates.get(permission.permissionKey) !== permission.isAllowed);
	}

	private async savePermissionChanges(): Promise<void> {
		try {
			for (const permission of this.permissionRows) {
				const originalValue = this.originalPermissionStates.get(permission.permissionKey);
				if (originalValue === permission.isAllowed) {
					continue;
				}

				if (permission.isAllowed) {
					await firstValueFrom(this.permissionService.assignPermission(this.groupId, permission.permissionKey));
				} else {
					await firstValueFrom(this.permissionService.revokePermission(this.groupId, permission.permissionKey));
				}

				this.originalPermissionStates.set(permission.permissionKey, permission.isAllowed);
			}
		} catch (error) {
			await this.reloadPermissions();
			throw error;
		}
	}

	private async reloadPermissions(): Promise<void> {
		const permissions = await firstValueFrom(this.permissionService.getGroupPermissions(this.groupId));
		this.permissionRows = this.toPermissionGridRows(permissions);
		this.originalPermissionStates = new Map(this.permissionRows.map(permission => [permission.permissionKey, permission.isAllowed]));
		this.assignedSelectedRowKeys = [];
		this.availableSelectedRowKeys = [];
	}

	private toPermissionGridRows(permissions: Permission[]): PermissionGridRow[] {
		return [...permissions]
			.map(permission => ({
				...permission,
				category: this.permissionCategory(permission),
				permissionCode: permissionCodesByKey.get(permission.permissionKey.toLowerCase()) ?? permission.permissionKey,
				displayPermissionName: this.permissionName(permission),
				displayPermissionDescription: this.permissionDescription(permission),
			}))
			.sort((left, right) => {
				const categoryCompare = left.category.localeCompare(right.category);
				if (categoryCompare !== 0) {
					return categoryCompare;
				}

				return left.displayPermissionName.localeCompare(right.displayPermissionName);
			});
	}

	private permissionName(permission: Permission): string {
		if (permission.permissionDescription?.trim()) {
			return permission.permissionName;
		}

		const permissionCode = permissionCodesByKey.get(permission.permissionKey.toLowerCase()) ?? permission.permissionKey;
		return this.readablePermissionName(permissionCode);
	}

	private permissionDescription(permission: Permission): string {
		if (permission.permissionDescription?.trim()) {
			return permission.permissionDescription;
		}

		return permission.permissionName;
	}

	private readablePermissionName(permissionCode: string): string {
		const displayName = permissionDisplayNamesByCode.get(permissionCode);
		if (displayName) {
			return displayName;
		}

		return permissionCode
			.split('_')
			.filter(part => !['AL3', 'AP', 'AS', 'UM', 'MI'].includes(part))
			.map(part => part.charAt(0) + part.slice(1).toLowerCase())
			.join(' ');
	}

	private permissionCategory(permission: Permission): string {
		return [permission.group_1, permission.group_2]
			.filter(group => !!group)
			.join(' / ') || 'Other';
	}

	private toPermissionKeys(selectedRowKeys: unknown[]): string[] {
		return (selectedRowKeys ?? []).filter((value): value is string => typeof value === 'string');
	}
}
